/**
 * Step 4: Upload Documents
 * Upload invoice, PO, COA, MSDS, BOL documents
 */

import React from 'react';
import {
  Box, Typography, Grid, Button, useTheme, alpha, Paper, Stack, Chip, Divider
} from '@mui/material';
import {
  Upload as UploadIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

export default function StepUploadDocuments({
  adjustmentType,
  documents,
  onDocumentsChange,
  config
}) {
  const theme = useTheme();
  const isProduct = config?.hasUnitConfig;
  const hasAnyProduct = config?.hasAnyProduct;

  const showProductDocs = isProduct || hasAnyProduct;

  const productDocTypes = [
    { key: 'sds', label: 'Safety Data Sheet (SDS)' },
    { key: 'production_doc', label: 'Production Document' }
  ];

  const allInventoryDocTypes = [
    { key: 'invoice', label: 'Invoice' },
    { key: 'po', label: 'Purchase Order' },
    { key: 'coa', label: 'Certificate of Analysis' },
    { key: 'msds', label: 'MSDS' },
    { key: 'bol', label: 'Bill of Lading' }
  ];

  // Product doc types: includes all relevant docs for product inventory
  const productOnlyDocTypes = [
    { key: 'invoice',        label: 'Invoice' },
    { key: 'po',             label: 'Purchase Order' },
    { key: 'coa',            label: 'Certificate of Analysis (COA)' },
    { key: 'sds',            label: 'Safety Data Sheet (SDS)' },
    { key: 'bol',            label: 'Bill of Lading' },
    { key: 'production_doc', label: 'Production Document' }
  ];

  const handleFileSelect = (docType, event) => {
    const file = event.target.files[0];
    if (file) {
      onDocumentsChange({
        ...documents,
        [docType]: file
      });
    }
  };

  const handleRemoveFile = (docType) => {
    const newDocs = { ...documents };
    delete newDocs[docType];
    onDocumentsChange(newDocs);
  };

  // Don't show this step for usage/transfer
  if (adjustmentType !== 'add') {
    return (
      <Box>
        <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.05rem' }}>
          Document upload is only available for Add Stock transactions.
        </Typography>
      </Box>
    );
  }

  const renderDocGrid = (docTypes) => (
    <Grid container spacing={3}>
      {docTypes.map((docType) => (
        <Grid item xs={12} md={6} key={docType.key}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
              '&:hover': {
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
              }
            }}
          >
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {docType.label}
                </Typography>
                {documents[docType.key] && (
                  <Chip icon={<DescriptionIcon />} label="Uploaded" color="success" size="small" />
                )}
              </Box>

              {documents[docType.key] ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {documents[docType.key].name}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleRemoveFile(docType.key)}
                    sx={{ borderRadius: 2 }}
                  >
                    Remove
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  Upload {docType.label}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileSelect(docType.key, e)}
                  />
                </Button>
              )}
            </Stack>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  // All-product selection: just show product-only layout (COA, SDS, Production Doc)
  if (isProduct) {
    return (
      <Box>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', fontSize: '1.05rem' }}>
          Upload supporting documents for this inventory addition (optional)
        </Typography>
        {renderDocGrid(productOnlyDocTypes)}
        <Typography variant="caption" sx={{ mt: 3, display: 'block', color: 'text.secondary' }}>
          Accepted formats: PDF, JPG, PNG (Max 10MB per file)
        </Typography>
      </Box>
    );
  }

  // Mixed or non-product selection
  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', fontSize: '1.05rem' }}>
        Upload supporting documents for this inventory addition (optional)
      </Typography>

      {/* Product Documents — only shown when products are in the selection */}
      {showProductDocs && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'primary.main', mb: 0.5 }}>
              Product Documents
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Applicable to product inventory items only
            </Typography>
          </Box>
          {renderDocGrid(productDocTypes)}

          <Divider sx={{ my: 4 }} />
        </>
      )}

      {/* All Inventory Documents */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'primary.main', mb: 0.5 }}>
          All Inventory Documents
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Applicable to all selected inventory items
        </Typography>
      </Box>
      {renderDocGrid(allInventoryDocTypes)}

      <Typography variant="caption" sx={{ mt: 3, display: 'block', color: 'text.secondary' }}>
        Accepted formats: PDF, JPG, PNG (Max 10MB per file)
      </Typography>
    </Box>
  );
}
